import React, { useState, useEffect } from 'react';
import { userApiClient } from '../../api/HttpClient';
import { getImageUrl } from '../../utils/imageUrl';
import { sendTenantInvite, getTenantAgreement } from '../../api/services/pgTenantService';
import { buildAgreementPdf } from './AgreementDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, User, Phone, Calendar, BadgeIndianRupee, Info, FileImage, FileText, Zap, DoorOpen, MessageCircle, Download } from 'lucide-react';
import AgreementDialog from './AgreementDialog';

interface TenantDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  propertyId?: string;
  roomNumber?: string;
}

interface TenantDetails {
  id: string;
  name: string;
  phone: string;
  identity_document_url: string;
  join_date: string;
  rent: number;
  advance_amount: number;
  status: string;
}

const TenantDetailsDialog: React.FC<TenantDetailsDialogProps> = ({ open, onClose, tenantId, propertyId = '', roomNumber = '' }) => {
  const [tenantDetails, setTenantDetails] = useState<TenantDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [electricityBills, setElectricityBills] = useState<any[]>([]);
  const [vacateNotice, setVacateNotice] = useState<any | null>(null);
  const [inviteSending, setInviteSending] = useState(false);
  const [viewingAgreement, setViewingAgreement] = useState(false);
  const [hasAgreement, setHasAgreement] = useState(false);

  useEffect(() => {
    if (open && tenantId) {
      const fetchAll = async () => {
        setLoading(true);
        setError(null);
        try {
          const client = await userApiClient;
          const [details, billsResp, noticesResp, agreementResp] = await Promise.all([
            client.get<TenantDetails>(`/api/pg_tenant/longterm/tenants/${tenantId}/`),
            propertyId
              ? client.get<any[]>(`/api/pg_tenant/property/${propertyId}/electricity-bills/`).catch(() => [])
              : Promise.resolve([]),
            propertyId
              ? client.get<any[]>(`/api/pg_tenant/property/${propertyId}/vacate-notices/`).catch(() => [])
              : Promise.resolve([]),
            getTenantAgreement(tenantId).catch(() => null),
          ]);
          setTenantDetails(details);
          const bills = Array.isArray(billsResp) ? billsResp : [];
          setElectricityBills(bills.filter((b: any) => b.tenant_id === tenantId));
          const notices = Array.isArray(noticesResp) ? noticesResp : [];
          setVacateNotice(notices.find((n: any) => n.tenant_id === tenantId) || null);
          setHasAgreement(!!(agreementResp && agreementResp.id));
        } catch (err: any) {
          console.error('Error fetching tenant details:', err);
          setError(err.response?.data?.detail || 'Failed to fetch tenant details.');
        } finally {
          setLoading(false);
        }
      };
      fetchAll();
    }
  }, [open, tenantId, propertyId]);

  const handleSendInvite = async () => {
    if (!tenantDetails) return;
    setInviteSending(true);
    try {
      const res = await sendTenantInvite(tenantId);
      const waUrl = res.whatsapp_url || `https://wa.me/91${tenantDetails.phone}?text=${encodeURIComponent(res.message)}`;
      window.open(waUrl, '_blank');
    } catch {
      alert('Failed to generate invite. Please try again.');
    } finally {
      setInviteSending(false);
    }
  };

  const handleViewAgreement = async () => {
    if (!tenantDetails) return;
    setViewingAgreement(true);
    try {
      const data = await getTenantAgreement(tenantId);
      if (!data || !data.id) { alert('No agreement found for this tenant.'); return; }
      const doc = buildAgreementPdf({
        pg_name: data.pg_name,
        owner_name: data.owner_name,
        tenant_name: data.tenant_name || tenantDetails.name,
        tenant_phone: data.tenant_phone || tenantDetails.phone,
        room_number: data.room_number,
        rent_amount: parseFloat(data.rent_amount),
        security_deposit: parseFloat(data.security_deposit),
        maintenance_charges: parseFloat(data.maintenance_charges || '0'),
        payment_due_day: data.payment_due_day,
        notice_period_months: data.notice_period_months,
        agreement_start_date: data.agreement_start_date,
        duration_months: data.duration_months,
        rules: data.rules,
        host_signature_data_url: data.host_signature || undefined,
        tenant_signature_data_url: data.tenant_signature || undefined,
      });
      const blobUrl = doc.output('bloburl');
      window.open(blobUrl as unknown as string, '_blank');
    } catch {
      alert('Failed to load agreement. Please try again.');
    } finally {
      setViewingAgreement(false);
    }
  };

  const DetailItem = ({ icon, label, value }: { icon: React.ReactNode, label: string, value: React.ReactNode }) => (
    <div className="flex items-start gap-3">
      <div className="text-muted-foreground mt-1">{icon}</div>
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="font-medium text-card-foreground">{value}</p>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-lg bg-card p-0 gap-0 overflow-hidden">
        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="text-lg font-bold text-[#276859]">Tenant Details</DialogTitle>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto max-h-[75vh] px-5 py-4 space-y-4">
          {loading ? (
            <div className="flex justify-center items-center h-48">
              <Loader2 className="h-8 w-8 animate-spin text-[#276859]" />
            </div>
          ) : error ? (
            <div className="text-center text-destructive bg-destructive/10 p-4 rounded-md text-sm">
              <p className="font-medium">Error fetching details</p>
              <p>{error}</p>
            </div>
          ) : tenantDetails ? (
            <>
              {/* Basic info — 2 column grid */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 bg-gray-50 rounded-xl p-4">
                <DetailItem icon={<User className="h-4 w-4" />} label="Name" value={tenantDetails.name} />
                <DetailItem icon={<Phone className="h-4 w-4" />} label="Phone" value={tenantDetails.phone} />
                <DetailItem icon={<Calendar className="h-4 w-4" />} label="Join Date" value={new Date(tenantDetails.join_date).toLocaleDateString('en-IN')} />
                <DetailItem icon={<Info className="h-4 w-4" />} label="Status" value={
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tenantDetails.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {tenantDetails.status}
                  </span>
                } />
                <DetailItem icon={<BadgeIndianRupee className="h-4 w-4" />} label="Rent" value={`₹ ${Number(tenantDetails.rent).toLocaleString('en-IN')}`} />
                <DetailItem icon={<BadgeIndianRupee className="h-4 w-4" />} label="Advance" value={`₹ ${Number(tenantDetails.advance_amount).toLocaleString('en-IN')}`} />
              </div>

              {/* ID Proof — compact */}
              {tenantDetails.identity_document_url && (
                <div className="rounded-xl border overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b">
                    <FileImage className="h-4 w-4 text-[#276859]" />
                    <p className="text-xs font-semibold text-[#276859]">ID Proof</p>
                  </div>
                  <div className="bg-white flex items-center justify-center p-2 max-h-40">
                    <img
                      src={getImageUrl(tenantDetails.identity_document_url)}
                      alt="ID Proof"
                      className="max-h-36 w-auto object-contain rounded"
                    />
                  </div>
                </div>
              )}

              {/* Vacate Notice */}
              {vacateNotice && (
                <div className="rounded-xl border border-orange-200 overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 border-b border-orange-200">
                    <DoorOpen className="h-4 w-4 text-orange-500" />
                    <p className="text-xs font-semibold text-orange-600">Vacate Notice</p>
                  </div>
                  <div className="p-3 text-xs space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Planned Vacate</span>
                      <span className="font-semibold text-gray-800">{new Date(vacateNotice.planned_vacate_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Moving to PG</span>
                      <span className="font-semibold text-gray-800">{vacateNotice.moving_to_pg ? 'Yes' : 'No'}</span>
                    </div>
                    {vacateNotice.next_area && <div className="flex justify-between"><span className="text-gray-500">Next Area</span><span className="font-semibold text-gray-800">{vacateNotice.next_area}</span></div>}
                    {vacateNotice.next_city && <div className="flex justify-between"><span className="text-gray-500">Next City</span><span className="font-semibold text-gray-800">{vacateNotice.next_city}</span></div>}
                  </div>
                </div>
              )}

              {/* Electricity Bills */}
              {electricityBills.length > 0 && (
                <div className="rounded-xl border overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b">
                    <Zap className="h-4 w-4 text-[#276859]" />
                    <p className="text-xs font-semibold text-[#276859]">Electricity Bills</p>
                  </div>
                  <div className="divide-y">
                    {electricityBills.slice(0, 6).map((bill: any) => {
                      const [year, month] = (bill.month || '').split('-');
                      const label = year && month ? new Date(Number(year), Number(month) - 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : bill.month;
                      return (
                        <div key={bill.id} className="flex items-center justify-between px-3 py-2 text-xs">
                          <div>
                            <p className="font-semibold text-gray-800">{label}</p>
                            {bill.units_consumed != null && <p className="text-gray-400">{bill.units_consumed} units</p>}
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-gray-800">₹{Number(bill.amount).toLocaleString('en-IN')}</p>
                            <span className={`font-semibold ${bill.is_paid ? 'text-green-600' : 'text-orange-500'}`}>{bill.is_paid ? 'Paid' : 'Pending'}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Action buttons — inside the scrollable body */}
              <div className="grid grid-cols-2 gap-2 pt-1">
                <Button
                  className="bg-[#25D366] hover:bg-[#1ebe57] text-white text-xs h-9"
                  onClick={handleSendInvite}
                  disabled={inviteSending}
                >
                  {inviteSending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <MessageCircle className="h-3 w-3 mr-1" />}
                  Tenant App Invite
                </Button>

                <Button
                  className="bg-[#276859] hover:bg-[#1f5246] text-white text-xs h-9"
                  onClick={() => setAgreementOpen(true)}
                >
                  <FileText className="h-3 w-3 mr-1" />
                  Generate Agreement
                </Button>

                {hasAgreement && (
                  <Button
                    variant="outline"
                    className="col-span-2 border-[#276859] text-[#276859] text-xs h-9"
                    onClick={handleViewAgreement}
                    disabled={viewingAgreement}
                  >
                    {viewingAgreement ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Download className="h-3 w-3 mr-1" />}
                    View & Download Agreement
                  </Button>
                )}
              </div>
            </>
          ) : (
            <div className="text-center text-muted-foreground py-12 text-sm">No tenant details found.</div>
          )}
        </div>

        {/* Footer — just Close */}
        <div className="px-5 py-3 border-t flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>

      {tenantDetails && (
        <AgreementDialog
          open={agreementOpen}
          onClose={() => setAgreementOpen(false)}
          tenantId={tenantId}
          propertyId={propertyId}
          tenantName={tenantDetails.name}
          tenantPhone={tenantDetails.phone}
          roomNumber={roomNumber}
          rent={tenantDetails.rent}
          advance={tenantDetails.advance_amount}
        />
      )}
    </Dialog>
  );
};

export default TenantDetailsDialog;